<script setup lang="ts">
import { useLazyAsyncData, useNuxtApp } from "#app";
import { computed, onMounted, onUnmounted, watch } from "vue";
import { useTipsPageStore } from "~/stores/tip/useTipsPageStore";
import InfiniteLoading from "v3-infinite-loading";
import { useTipsMetaPageStore } from "~/stores/page/useTipsMetaPageStore";
import { clearAdsenseBlocks } from "~/utils/adsense";

const tipsPageStore = useTipsPageStore();
const tipsMetaPageStore = useTipsMetaPageStore();
let page = 1;
const search = ref('');

await useLazyAsyncData('tipsMetaPageStore', async () => tipsMetaPageStore.fetch());
await useLazyAsyncData('tipsPageStore', async () => tipsPageStore.fetchList(page));
if (typeof tipsPageStore.error === "object") {
    throw tipsPageStore.error;
}

let timeout;
watch(search, (newValue) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => {
        page = 1;
        tipsPageStore.fetchList(page, String(newValue));
    }, 300);
});

const tips = computed(() => tipsPageStore.getList);
const textAfterContent = computed(() => tipsMetaPageStore.textAfterContent ? useNuxtApp().marked().parse(tipsMetaPageStore.textAfterContent) : '');
const pagination = computed(() => tipsPageStore.pagination);
const loadMore = ($state) => {
    if (tips.value === undefined || pagination.value?.pageCount < page) {
        return;
    }

    page++;
    tipsPageStore.fetchList(page, search.value);
}

onMounted(() => {
    window.scrollTo(0, 0);
});

onUnmounted(() => {
    clearAdsenseBlocks();
    tipsPageStore.clear();
});
</script>

<template>
    <section class="container">
<!--        <ClientOnly>
            <section class="flex container justify-center mb-6">
                <AdsenseSmallHorizontal/>
            </section>
        </ClientOnly>-->
        <div v-if="tips !== undefined" class="space-y-12 lg:space-y-16">
            <div class="max-w-2xl mx-auto">
                <form>
                    <div class="relative">
                        <div class="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                            <svg class="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
                        </div>
                        <input type="search" v-model="search" id="default-search" class="block p-4 pl-10 w-full text-md text-gray-900 bg-gray-50 rounded-lg focus:ring-indigo-600 focus:border-indigo-500" placeholder="Search tips, start writing..." required>
<!--                        <button type="submit" class="text-white absolute right-2.5 bottom-2.5 bg-indigo-700 hover:bg-indigo-800 focus:ring-4 focus:outline-none focus:ring-indigo-600 font-medium rounded-lg text-sm px-4 py-2">Search</button>-->
                    </div>
                </form>
            </div>
            <div v-if="!tips.length" class="text-center">
                <p>Try changing your search criteria.</p>
            </div>
            <div v-else class="masonry sm:masonry-sm lg:masonry-md space-y-6">
                <div v-for="(tip, index) in tips"
                     :key="tip.slug"
                     :class="`break-inside bg-${tip.color}-50 rounded-lg p-4 space-y-2`"
                >
                    <h4 class="text-gray-800 text-lg font-bold">{{ tip.title }}</h4>
                    <p class="text-gray-800 text-md" v-html="tip.text"></p>
                    <ClientOnly v-if="index%10 === 0">
                        <AdsenseMediaBigWidth/>
                    </ClientOnly>
                </div>
            </div>
        </div>
        <div v-else class="space-y-16">
            <div class="max-w-2xl mx-auto">
                <form>
                    <div class="relative">
                        <div class="animate-pulse h-[3.5rem] w-full object-center rounded-lg bg-gray-100"></div>
                    </div>
                </form>
            </div>

            <div class="masonry sm:masonry-sm lg:masonry-md space-y-6">
                <div v-for="index in 24" :key="index" class="break-inside bg-gray-50 rounded-lg p-4 space-y-2">
                    <h4 class="animate-pulse rounded h-6 w-[40%] bg-gray-100"></h4>
                    <p class="animate-pulse rounded h-4 w-[70%] bg-gray-100"></p>
                    <p class="animate-pulse rounded h-4 w-[60%] bg-gray-100"></p>
                    <p class="animate-pulse rounded h-4 w-[80%] bg-gray-100"></p>
                    <p class="animate-pulse rounded h-4 w-[50%] bg-gray-100"></p>
                </div>
            </div>
        </div>
    </section>
    <ClientOnly>
        <InfiniteLoading @infinite="loadMore">
            <template #spinner><span></span></template>
        </InfiniteLoading>
    </ClientOnly>
    <section class="container">
        <article class="space-y-4 prose min-w-full" v-html="textAfterContent"></article>
    </section>
</template>